import { useEffect, useMemo, useRef, useState } from "react";
import { useApiFetch } from "../../api/client";
import { Card } from "../../components/ui/Card";
import { Chip } from "../../components/ui/Chip";
import { errorMessage, monthName, recordedLabel, shortDateLabel } from "./format";
import { SectionHeader } from "./SectionHeader";
import {
  activeNotesNewestFirst,
  EXPECTATION_MEASURE_LABELS,
  expectationSpreads,
  filterHeadlines,
  formatExpectationRange,
  latestExpectationPerPublisher,
} from "./marketIntelligenceViews";
import {
  expectationDrawerContent,
  headlineDrawerContent,
  noteDrawerContent,
  type SourceDrawerContent,
} from "./sourceDrawerContent";
import type { Headline, Note, PublisherView } from "./types";

interface Props {
  month: string;
  resinFamilyCode: string;
  logIntelligenceVersion: number;
  onOpenDrawer: (content: SourceDrawerContent) => void;
}

interface LoadedIntelligence {
  loadKey: string;
  headlines: Headline[];
  expectations: PublisherView[];
  notes: Note[];
}

const EMPTY_TEXT_CLASSES = "m-0 text-rs-light-gray text-[13px]";
const SCROLLING_LIST_CLASSES = "flex flex-col max-h-[420px] overflow-y-auto";
const LIST_ROW_CLASSES =
  "flex flex-col gap-1 w-full text-left bg-transparent border-0 border-b border-rs-jet last:border-b-0 px-2 py-2.5 rounded-sm cursor-pointer hover:bg-rs-jet";

function firstCode(record: Headline | Note): string | null {
  return record.designationCode ?? record.baseCode ?? record.additiveCode ?? record.colorCode ?? null;
}

function HeadlineRow({ headline, onOpen }: { headline: Headline; onOpen: () => void }) {
  const code = firstCode(headline);
  return (
    <button type="button" className={LIST_ROW_CLASSES} onClick={onOpen}>
      <span className="text-sm text-rs-off-white">{headline.title}</span>
      <span className="flex flex-wrap items-center gap-2 text-xs text-rs-light-gray">
        <span>
          {headline.publisherName} · {shortDateLabel(headline.publishedDate)}
          {headline.regionCode ? ` · ${headline.regionCode}` : ""}
        </span>
        {code && <Chip variant="ok">{code}</Chip>}
      </span>
    </button>
  );
}

function ExpectationsPanel({
  expectations,
  onOpenExpectation,
}: {
  expectations: PublisherView[];
  onOpenExpectation: (expectation: PublisherView) => void;
}) {
  const latestExpectations = latestExpectationPerPublisher(expectations);
  if (latestExpectations.length === 0) {
    return <p className={EMPTY_TEXT_CLASSES}>No publisher expectations logged for this month.</p>;
  }
  return (
    <>
      <div className="flex flex-col gap-1.5 mb-3">
        {expectationSpreads(latestExpectations).map((spread) => (
          <div key={spread.measureCode} className="text-[13px] text-rs-light-gray">
            {EXPECTATION_MEASURE_LABELS[spread.measureCode]} ·{" "}
            <strong className="text-rs-green">
              {formatExpectationRange(spread.measureCode, spread.lowestValue, spread.highestValue)}
            </strong>{" "}
            · {spread.publisherCount} {spread.publisherCount === 1 ? "publisher" : "publishers"}
          </div>
        ))}
      </div>
      <div className="flex flex-col">
        {latestExpectations.map((expectation) => (
          <button
            type="button"
            key={`${expectation.publisherName}|${expectation.measureCode}`}
            className="flex flex-wrap items-baseline justify-between gap-2 w-full text-left bg-transparent border-0 border-t border-rs-jet px-2 py-2 rounded-sm cursor-pointer hover:bg-rs-jet"
            onClick={() => onOpenExpectation(expectation)}
          >
            <span className="text-sm text-rs-off-white">
              {expectation.publisherName}
              <small className="ml-2 text-xs text-rs-light-gray">
                {EXPECTATION_MEASURE_LABELS[expectation.measureCode]}
              </small>
            </span>
            <span className="text-[13px] text-rs-light-gray">
              <strong className="text-rs-off-white">
                {formatExpectationRange(expectation.measureCode, expectation.lowValue, expectation.highValue)}
              </strong>{" "}
              · as of {shortDateLabel(expectation.asOfDate)}
            </span>
          </button>
        ))}
      </div>
    </>
  );
}

function NoteRow({ note, onOpen }: { note: Note; onOpen: () => void }) {
  const code = firstCode(note);
  return (
    <button type="button" className={LIST_ROW_CLASSES} onClick={onOpen}>
      <span className="text-sm text-rs-off-white whitespace-pre-wrap">{note.body}</span>
      <span className="flex flex-wrap items-center gap-2 text-xs text-rs-light-gray">
        <span>
          {note.enteredByDisplayName} · {recordedLabel(note.recordedUtc)}
        </span>
        {code && <Chip variant="ok">{code}</Chip>}
      </span>
    </button>
  );
}

export function MarketIntelligence({ month, resinFamilyCode, logIntelligenceVersion, onOpenDrawer }: Props) {
  const apiFetch = useApiFetch();
  const apiFetchRef = useRef(apiFetch);
  apiFetchRef.current = apiFetch;
  const loadKey = `${resinFamilyCode}|${month}`;
  const [loadedIntelligence, setLoadedIntelligence] = useState<LoadedIntelligence | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [headlineFilterText, setHeadlineFilterText] = useState("");

  useEffect(() => {
    let isStale = false;
    const monthAndResinQuery = `month=${month}-01&resinFamily=${encodeURIComponent(resinFamilyCode)}`;
    Promise.all([
      apiFetchRef.current(`consultant-cockpit/headlines?${monthAndResinQuery}`).then((response) => response.json()),
      apiFetchRef.current(`consultant-cockpit/forecasts?${monthAndResinQuery}`).then((response) => response.json()),
      apiFetchRef.current(`consultant-cockpit/notes?${monthAndResinQuery}`).then((response) => response.json()),
    ])
      .then(([headlines, expectations, notes]: [Headline[], PublisherView[], Note[]]) => {
        if (isStale) return;
        setLoadedIntelligence({ loadKey, headlines, expectations, notes });
        setLoadError(null);
      })
      .catch((error) => {
        if (!isStale) setLoadError(errorMessage(error));
      });
    return () => {
      isStale = true;
    };
  }, [month, resinFamilyCode, loadKey, logIntelligenceVersion]);

  const currentIntelligence = loadedIntelligence?.loadKey === loadKey ? loadedIntelligence : null;
  const visibleHeadlines = useMemo(
    () => (currentIntelligence ? filterHeadlines(currentIntelligence.headlines, headlineFilterText) : []),
    [currentIntelligence, headlineFilterText],
  );
  const visibleNotes = useMemo(
    () => (currentIntelligence ? activeNotesNewestFirst(currentIntelligence.notes) : []),
    [currentIntelligence],
  );

  return (
    <section className="lane on" aria-labelledby="intelligence-heading">
      <SectionHeader sectionId="intelligence-section">
        <Chip>{monthName(month)}</Chip>
        <Chip variant="ok">{resinFamilyCode}</Chip>
      </SectionHeader>

      {loadError ? (
        <Card>
          <p className={EMPTY_TEXT_CLASSES}>Couldn't load market intelligence: {loadError}</p>
        </Card>
      ) : !currentIntelligence ? (
        <Card>
          <p className={EMPTY_TEXT_CLASSES}>Loading…</p>
        </Card>
      ) : (
        <div className="grid gap-6 lg:grid-cols-2">
          <Card className="min-w-0">
            <h3 className="m-0 mb-3 text-[15px]">Headlines</h3>
            <input
              type="search"
              className="ctl mb-3"
              placeholder="Filter by title, publisher or code"
              aria-label="Filter headlines"
              value={headlineFilterText}
              onChange={(event) => setHeadlineFilterText(event.target.value)}
            />
            {visibleHeadlines.length === 0 ? (
              <p className={EMPTY_TEXT_CLASSES}>
                {headlineFilterText.trim()
                  ? "No headlines match that filter."
                  : "No headlines logged for this month."}
              </p>
            ) : (
              <div className={SCROLLING_LIST_CLASSES}>
                {visibleHeadlines.map((headline) => (
                  <HeadlineRow
                    key={headline.marketHeadlineId}
                    headline={headline}
                    onOpen={() => onOpenDrawer(headlineDrawerContent(headline))}
                  />
                ))}
              </div>
            )}
          </Card>

          <div className="flex flex-col gap-6 min-w-0">
            <Card>
              <h3 className="m-0 mb-3 text-[15px]">Publisher Expectations</h3>
              <ExpectationsPanel
                expectations={currentIntelligence.expectations}
                onOpenExpectation={(expectation) => onOpenDrawer(expectationDrawerContent(expectation))}
              />
            </Card>
            <Card>
              <h3 className="m-0 mb-3 text-[15px]">Consultant Notes</h3>
              {visibleNotes.length === 0 ? (
                <p className={EMPTY_TEXT_CLASSES}>No notes for this month.</p>
              ) : (
                <div className={SCROLLING_LIST_CLASSES}>
                  {visibleNotes.map((note) => (
                    <NoteRow
                      key={note.consultantNoteId}
                      note={note}
                      onOpen={() => onOpenDrawer(noteDrawerContent(note))}
                    />
                  ))}
                </div>
              )}
            </Card>
          </div>
        </div>
      )}
    </section>
  );
}
